import { useEffect, type RefObject } from 'react';

// Modal focus management (a11y — WCAG 2.4.3 / 2.1.2). When `active`, this hook:
//   1. remembers the element that had focus before the modal opened,
//   2. moves focus into the modal (a [data-autofocus] element if present, else the first
//      focusable, else the container itself),
//   3. traps Tab/Shift+Tab inside the container (wraps at both ends), and
//   4. restores focus to the original element on close/unmount.
// Esc-to-close is left to each dialog (semantics differ — cancel vs dismiss).
//
// NOTE: this is a tiny self-contained hook duplicated per standalone app (no shared
// package across the 5 apps). Keep copies in sync if changed.
export function useFocusTrap(active: boolean, containerRef: RefObject<HTMLElement | null>): void {
  useEffect(() => {
    if (!active) return;
    const container = containerRef.current;
    if (!container) return;
    const previouslyFocused = document.activeElement as HTMLElement | null;

    const focusable = (): HTMLElement[] =>
      Array.from(
        container.querySelectorAll<HTMLElement>(
          'a[href],button:not([disabled]),input:not([disabled]),select:not([disabled]),textarea:not([disabled]),[tabindex]:not([tabindex="-1"])'
        )
      ).filter((el) => el.offsetParent !== null || el === document.activeElement);

    const initial =
      container.querySelector<HTMLElement>('[data-autofocus]') ?? focusable()[0] ?? container;
    initial.focus();

    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key !== 'Tab') return;
      const items = focusable();
      if (items.length === 0) {
        e.preventDefault();
        return;
      }
      const first = items[0];
      const last = items[items.length - 1];
      const current = document.activeElement as HTMLElement | null;
      if (e.shiftKey) {
        if (current === first || !container.contains(current)) {
          e.preventDefault();
          last.focus();
        }
      } else if (current === last || !container.contains(current)) {
        e.preventDefault();
        first.focus();
      }
    };

    container.addEventListener('keydown', onKeyDown);
    return () => {
      container.removeEventListener('keydown', onKeyDown);
      previouslyFocused?.focus?.();
    };
  }, [active, containerRef]);
}
